import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import moment from "moment";
import { getFinanceRevenue, getFinanceRevenueBilledTo, getFinanceTopOffices } from "../../../redux/slices/FinanceSlice";
import { useSelector } from "react-redux";
import { billedToOptions, formatCurrency } from "../../../utils";
import { FinanceBilledTo, TopOffice } from "../../../models/finance.model";

const monthName = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const DashboardTab = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [monthOptions, setMonthOptions] = useState<string[]>([""]);
  const [selectedMonth, setSelectedMonth] = useState<string>(``);

  const { financeRevenue, financeRevenueBilledTo, financeTopOffices } = useSelector((state: RootState) => state.finance);

  const handleMonthChange = (e: string) => {
    setSelectedMonth(e);
    const splitMonth = e.split(" ");
    const month = monthName.indexOf(splitMonth[0]);
    const year = splitMonth[1];
    const firstDay = moment([year, month]).startOf("month").format("YYYY-MM-DD");
    const lastDay = moment([year, month]).endOf("month").add(1, "day").format("YYYY-MM-DD");
    dispatch(
      getFinanceRevenue({
        startDate: new Date(firstDay),
        endDate: new Date(lastDay),
      })
    );
    dispatch(
      getFinanceRevenueBilledTo({
        startDate: new Date(firstDay),
        endDate: new Date(lastDay),
      })
    );
    dispatch(
      getFinanceTopOffices({
        startDate: new Date(firstDay),
        endDate: new Date(lastDay),
      })
    );
  };

  useEffect(() => {
    const date = new Date();
    date.setDate(1);
    const monthArray: string[] = [];
    for (let i = 0; i <= 11; i++) {
      monthArray.push(monthName[date.getMonth()] + " " + date.getFullYear());
      date.setMonth(date.getMonth() - 1);
    }
    handleMonthChange(monthArray[0]);
    setMonthOptions(monthArray);
  }, []);

  return (
    <div>
      <div className="p-5 bg-docsigna-pink-light rounded-xl mb-5">
        <div className="flex flex-wrap justify-between items-center mb-3">
          <h2 className="text-2xl font-semibold heading uppercase text-docsigna-purple">Docnote Revenue</h2>
          <div className="flex flex-wrap gap-5 items-center">
            <p className="font-bold text-base">Month Being Viewed</p>
            <div className="flex items-center justify-start p-0 xl:mb-0 mt-2 sm:mt-0">
              <select
                onChange={e => {
                  handleMonthChange(e.target.value);
                }}
                value={selectedMonth}
                className={`general-select w-52 space-x-0`}>
                {monthOptions.map((ele, index) => {
                  return <option key={index}>{ele}</option>;
                })}
              </select>
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-1">
          <div className="flex gap-5 w-full">
            <div className="flex justify-between pb-1 gap-2 border-b border-docsigna-purple border-dotted text-base font-medium w-full">
              <h3 className="">
                Total Docnote Request Fee Revenue <span className="text-xs italic text-nowrap">(incl. physician fee)</span>
              </h3>
              <h5 className="">
                {financeRevenue?.totalDnRequestFeeRevenue
                  ? formatCurrency((financeRevenue?.totalDnRequestFeeRevenue / 100)?.toString(), "blur")
                  : "$0.00"}
              </h5>
            </div>
          </div>
          <div className="flex gap-5 w-full">
            <div className="flex justify-between pb-1 border-b border-docsigna-purple border-dotted text-base font-medium w-full">
              <h3 className="">Docnote Fees Collected in Stripe</h3>
              <h5 className="">
                {financeRevenue?.dnFeesCollectedInStripe
                  ? formatCurrency((financeRevenue?.dnFeesCollectedInStripe / 100).toString(), "blur")
                  : "$0.00"}
              </h5>
            </div>
          </div>
          <div className="flex gap-5 w-full">
            <div className="flex justify-between pb-1 border-b border-docsigna-purple border-dotted text-base font-medium w-full">
              <h3 className="">Total Docnote Annual Plan Fee Revenue</h3>
              <h5 className="">
                {financeRevenue?.totalDnAnnualPlanFeeRevenue
                  ? formatCurrency((financeRevenue?.totalDnAnnualPlanFeeRevenue / 100).toString(), "blur")
                  : "$0.00"}
              </h5>
            </div>
          </div>
          <div className="flex gap-5 w-full">
            <div className="flex justify-between pb-1 border-b border-docsigna-purple border-dotted text-base font-medium w-full">
              <h3 className="">Docnote Fees Collected in Office</h3>
              <h5 className="">
                {financeRevenue?.dnFeesCollectedInOffice
                  ? formatCurrency((financeRevenue?.dnFeesCollectedInOffice / 100).toString(), "blur")
                  : "$0.00"}
              </h5>
            </div>
          </div>
          <div className="flex gap-5 w-full">
            <div className="flex justify-between pb-1 border-b border-docsigna-purple border-dotted text-base font-medium w-full">
              <h3 className="">Docnote HST Collected</h3>
              <h5 className="">
                {financeRevenue?.dnHstCollected ? formatCurrency((financeRevenue?.dnHstCollected / 100).toString(), "blur") : "$0.00"}
              </h5>
            </div>
          </div>
          <div className="flex gap-5 w-full">
            <div className="flex justify-between pb-1 border-b border-docsigna-purple border-dotted text-base font-medium w-full">
              <h3 className="">Docnote Fees Refunded</h3>
              <h5 className="">
                {financeRevenue?.dnFeesRefunded ? formatCurrency((financeRevenue?.dnFeesRefunded / 100).toString(), "blur") : "$0.00"}
              </h5>
            </div>
          </div>
          <div className="flex gap-5 w-full">
            <div className="flex justify-between pb-1 text-base font-medium w-full">
              <h3 className="">HST Flowthrough</h3>
              <h5 className="">
                {financeRevenue?.hstFlowThrought ? formatCurrency((financeRevenue?.hstFlowThrought / 100).toString(), "blur") : "$0.00"}
              </h5>
            </div>
          </div>
          <div className="flex gap-5 w-full">
            <div className="flex justify-between pb-1 text-base font-medium w-full">
              <h3 className="">
                Total Docnote Revenue <span className="text-xs italic text-nowrap">(incl. physician fee)</span>
              </h3>
              <h5 className="">
                {financeRevenue?.totalDnRevenue ? formatCurrency((financeRevenue?.totalDnRevenue / 100).toString(), "blur") : "$0.00"}
              </h5>
            </div>
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-x-10">
        {financeRevenueBilledTo?.length ? (
          <div>
            <h2 className="text-2xl font-semibold heading text-docsigna-purple uppercase">Docnote Revenue By billed to type</h2>
            <ul className="text-lg text-docsigna-purple font-bold">
              {financeRevenueBilledTo.map((item: FinanceBilledTo) => {
                return (
                  <li key={item.billedTo} className="grid grid-cols-2 border-b border-dotted border-docsigna-purple py-5 px-5">
                    <div>
                      <p>{billedToOptions[item.billedTo]}</p>
                    </div>
                    <div className="flex justify-between col-span-1">
                      <p>{item.count}</p>
                      <p>{formatCurrency((item.amount / 100).toString(), "blur")}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        <div>
          {financeTopOffices?.length ? (
            <div className="p-5 bg-docsigna-pink-light rounded-xl mb-5">
              <h2 className="font-bold text-xl heading text-docsigna-purple uppercase flex gap-2">
                TOP PRODUCING OFFICES <p className="capitalize">(Current Month)</p>
              </h2>
              <ul className="pr-5 mt-2 flex flex-col gap-1 font-medium text-lg">
                {financeTopOffices.map((item: TopOffice, i: number) => {
                  return (
                    <li key={i} className="flex justify-between items-center">
                      <p>{item.officeName}</p>
                      <p>{formatCurrency((item.amount / 100).toString(), "blur")}</p>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
          {financeRevenue?.numberOfOffices || financeRevenue?.numberOfRequests ? (
            <div className="p-10 bg-docsigna-purple rounded-xl flex flex-col justify-between gap-5 text-center text-white">
              <div>
                <p className="font-bold text-xl text-docnote-grey">NUMBER OF OFFICES</p>
                <p className="font-bold text-[100px] leading-none text-docnote-grey">{financeRevenue?.numberOfOffices}</p>
              </div>
              <div>
                <p className="font-bold text-xl text-docnote-grey">NUMBER OF REQUESTS</p>
                <p className="font-bold text-[100px] leading-none text-docnote-grey">{financeRevenue?.numberOfRequests}</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
